import React from "react";
import { useUpdateScrapbookPostMutation } from "../../../store/slices/api";
import { CATEGORIES } from "./categories";
import StyleFields from "./styles/StyleFields";

/**
 * Fixing the words on a post you already made.
 *
 * WHY IT IS ITS OWN FILE
 * ----------------------
 * The first version put a caption box inside PostCard and stopped there, which
 * left six of the seven fields the server accepts unreachable: a member who
 * mistyped a margin note still had to DELETE the post to fix it, which cascades
 * and takes every comment with it. That is the exact problem editing exists to
 * solve, so solving only the caption was solving the easy half.
 *
 * WHAT IS NOT HERE, AND WHY
 * -------------------------
 * The STYLE itself -- which of the nine templates the post uses. Its per-style
 * boxes are editable below, but swapping Snapshot for Pop-Up Book is a different
 * act: it changes how the post is drawn, and the server can refuse it outright
 * because a template is checked against the photographs already on the post,
 * which cannot change here. That belongs behind the picker, not in a panel on
 * the wall. Everything else the server accepts is reachable.
 */
const INPUT =
  "w-full px-[10px] py-[7px] rounded-[3px] font-body text-[15px] bg-white dark:bg-neutral-900 " +
  "border border-gray-300 dark:border-neutral-700 text-gray-800 dark:text-gray-200 " +
  "placeholder:italic placeholder:text-gray-400 focus:outline-none " +
  "focus:border-dupontBlue dark:focus:border-dupontGoldLt focus:ring-2 " +
  "focus:ring-dupontBlue/40 dark:focus:ring-dupontGoldLt/40";

const LABEL =
  "block font-blairBody text-[12px] font-semibold tracking-[0.04em] text-gray-600 dark:text-gray-400 mb-[3px]";

function EditPost({ post, onClose }) {
  const [updatePost, { isLoading: saving }] = useUpdateScrapbookPostMutation();

  // Seeded once from the post. Everything the server will accept, so nothing a
  // member can see is beyond their reach to correct.
  const [draft, setDraft] = React.useState(() => ({
    caption: post.caption || "",
    place: post.place || "",
    when_text: post.when_text || "",
    extra_note: post.extra_note || "",
    category: post.category || "memories",
    style_details: post.style_details || {},
  }));
  const [failed, setFailed] = React.useState("");

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const save = async () => {
    setFailed("");
    try {
      await updatePost({ postId: post.id, ...draft }).unwrap();
      onClose();
    } catch (err) {
      // Said out loud, and the panel stays open with the words still in it. A
      // save that quietly does nothing is worse than a refusal, because the
      // writer walks away believing it landed.
      setFailed(err?.data?.error || "That did not save. Try again in a moment.");
    }
  };

  return (
    <div
      className="pt-[11px]"
      onKeyDown={(e) => {
        // Escape abandons; Cmd/Ctrl+Enter saves from anywhere in the panel.
        // Plain Enter stays a new line, because these are sentences about a
        // photograph and not a search box.
        if (e.key === "Escape") onClose();
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
      }}
    >
      <label className={LABEL} htmlFor={`caption-${post.id}`}>
        The words under this post
      </label>
      <textarea
        id={`caption-${post.id}`}
        value={draft.caption}
        maxLength={500}
        rows={3}
        autoFocus
        onChange={set("caption")}
        className={`${INPUT} resize-y leading-[1.5] text-[18px]`}
      />
      <div className="text-right font-blairBody text-[12px] text-gray-400 mt-[2px]">
        {draft.caption.length}/500
      </div>

      {/* The margin note. Free text on purpose — a date picker refuses "1987 (?)"
          and "the year we all wore white", which is most of what anybody knows
          about an old photograph. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-[9px] mt-[9px]">
        <div>
          <label className={LABEL} htmlFor={`place-${post.id}`}>Where</label>
          <input id={`place-${post.id}`} value={draft.place} maxLength={120}
                 onChange={set("place")} placeholder="Hagley" className={INPUT} />
        </div>
        <div>
          <label className={LABEL} htmlFor={`when-${post.id}`}>When</label>
          <input id={`when-${post.id}`} value={draft.when_text} maxLength={120}
                 onChange={set("when_text")} placeholder="1987 (?)" className={INPUT} />
        </div>
        <div>
          <label className={LABEL} htmlFor={`extra-${post.id}`}>What else</label>
          <input id={`extra-${post.id}`} value={draft.extra_note} maxLength={120}
                 onChange={set("extra_note")} placeholder="Found behind a frame" className={INPUT} />
        </div>
      </div>

      <div className="mt-[9px] max-w-[260px]">
        <label className={LABEL} htmlFor={`category-${post.id}`}>Filed under</label>
        {/* A select, not a text box. The server now REFUSES a category it does
            not recognise rather than quietly refiling the post under Memories,
            so a free-text field here would only produce errors. */}
        <select id={`category-${post.id}`} value={draft.category}
                onChange={set("category")} className={INPUT}>
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Whatever this template asks for — a Then & Now's two years, a Did You
          Know?'s fact and source, an Album Page's line per photograph. The same
          component the posting form uses, so the two cannot drift apart. */}
      <div className="mt-[11px]">
        <StyleFields
          style={post.style}
          details={draft.style_details}
          attachments={post.attachments}
          onChange={(style_details) => setDraft((d) => ({ ...d, style_details }))}
        />
      </div>

      <div className="flex items-center gap-[10px] pt-[11px]">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="font-body text-[13px] font-semibold text-white bg-dupontBlue
                     disabled:opacity-60 rounded-[3px] px-[13px] py-[5px]
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-dupontBlue"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="font-body text-[13px] text-gray-500 hover:text-gray-800
                     dark:hover:text-gray-200 rounded-[2px]
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-gray-500"
        >
          Cancel
        </button>
        <span className="font-blairBody text-[12px] text-gray-400 ml-auto">
          The photographs stay as they are
        </span>
      </div>

      {failed && (
        <p role="alert" className="font-body text-[13px] text-red-600 pt-[6px]">
          {failed}
        </p>
      )}
    </div>
  );
}

export default EditPost;
